<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { ValidData as UploadingFilesDialogFormValidData } from '@/components/dialogForms/UploadingFilesDialogForm.vue';
import type { HeadersData } from '@/composables/useTable';
import type { DefDialogState, DialogName } from '@/stores/useDialogStore';

import { computed, ref } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchPostJobAttachment } from '@/helpers/fetchers';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  jobId: number;
  jobDate: string;
  customerCode: string;
  reference1: string;
  reference2: string;
  stop1name: string;
  stop1suburb: string;
  stop1state: string;
  stop2name: string;
  stop2suburb: string;
  stop2state: string;
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'jobId',
      title: 'Job ID',
    },
    {
      value: 'jobDate',
      title: 'Job Date',
    },
    {
      value: 'customerCode',
      title: 'Customer Code',
    },
    {
      value: 'reference1',
      title: 'Reference 1',
    },
    {
      value: 'reference2',
      title: 'Reference 2',
    },
    {
      value: 'stop1name',
      title: 'Stop 1 Name',
    },
    {
      value: 'stop1suburb',
      title: 'Stop 1 Suburb',
    },
    {
      value: 'stop1state',
      title: 'Stop 1 State',
    },
    {
      value: 'stop2name',
      title: 'Stop 2 Name',
    },
    {
      value: 'stop2suburb',
      title: 'Stop 2 Suburb',
    },
    {
      value: 'stop2state',
      title: 'Stop 2 State',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

const attachFilesActive = ref<Record<string, boolean>>({});
const attachFilesCount = ref<Record<string, number>>({});

const dialogStore = useDialogStore();

function handleAttachFilesClick(jobId: number, jobDate: string): void {
  attachFilesActive.value[jobId] = true;
  const dialogName: DialogName = 'uploadingFiles';
  dialogStore
    .open(dialogName, {
      confirm: async (formData: UploadingFilesDialogFormValidData) => {
        return fetchPostJobAttachment({
          rh_id: jobId,
          rh_date: jobDate,
          files: formData.attachments,
        }).then((data) => {
          if (!data.saved) return;
          attachFilesCount.value[jobId] = data.saved.length;
        });
      },
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        attachFilesActive.value[jobId] = false;
      },
    );
}
</script>

<template>
  <table
    :class="[tableStyles.table, $style.spreadsheetTable]"
    data-testid="spreadsheetTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <th :class="$style.fileAttachmentsTh">File attachments</th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.jobId">
        <tr>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="dataItem.jobId"
              :job-date="dataItem.jobDate"
            />
          </td>
          <td :class="$style.jobDateTd">
            {{ dataItem.jobDate }}
          </td>
          <td :class="$style.customerCodeTd">
            {{ dataItem.customerCode }}
          </td>
          <td :class="$style.reference1Td">
            {{ dataItem.reference1 }}
          </td>
          <td :class="$style.reference2Td">
            {{ dataItem.reference2 }}
          </td>
          <td :class="$style.stop1nameTd">
            {{ dataItem.stop1name }}
          </td>
          <td :class="$style.stop1suburbTd">
            {{ dataItem.stop1suburb }}
          </td>
          <td :class="$style.stop1stateTd">
            {{ dataItem.stop1state }}
          </td>
          <td :class="$style.stop2nameTd">
            {{ dataItem.stop2name }}
          </td>
          <td :class="$style.stop2suburbTd">
            {{ dataItem.stop2suburb }}
          </td>
          <td :class="$style.stop2stateTd">
            {{ dataItem.stop2state }}
          </td>
          <td :class="$style.fileAttachmentsTd">
            <template v-if="attachFilesCount[dataItem.jobId]">
              Files uploaded: {{ attachFilesCount[dataItem.jobId] }}
            </template>
            <template v-else>
              <menu :class="$style.actionsMenuList">
                <li :class="$style.actionsMenuListItem">
                  <KBtn
                    size="xs"
                    variant="outlined"
                    :active="attachFilesActive[dataItem.jobId]"
                    :class="$style.attachFilesBtn"
                    @click="
                      handleAttachFilesClick(dataItem.jobId, dataItem.jobDate)
                    "
                  >
                    Attach files
                  </KBtn>
                </li>
              </menu>
            </template>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.spreadsheetTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .jobIdTh {
  }
  .jobDateTh {
  }
  .customerCodeTh {
  }
  .reference1Th {
  }
  .reference2Th {
  }
  .stop1nameTh {
  }
  .stop1suburbTh {
  }
  .stop1stateTh {
  }
  .stop2nameTh {
  }
  .stop2suburbTh {
  }
  .stop2stateTh {
  }
  .fileAttachmentsTh {
  }
  .jobIdTd {
  }
  .jobDateTd {
  }
  .customerCodeTd {
  }
  .reference1Td {
  }
  .reference2Td {
  }
  .stop1nameTd {
  }
  .stop1suburbTd {
  }
  .stop1stateTd {
  }
  .stop2nameTd {
  }
  .stop2suburbTd {
  }
  .stop2stateTd {
  }
  .fileAttachmentsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
        .attachFilesBtn {
          :global {
            .v-btn__append {
              display: none;
            }
          }
        }
</style>
